"use client";

import type { KyanIcon, MarketIconSymbol } from "@kyan/ui";
import {
  cn,
  Icon,
  Select,
  SelectItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@kyan/ui";

import { MarginRatio } from "#/components/MarginRatio/MarginRatio";
import { useMarket } from "#/store/app/portfolio/market";
import { useAccountState } from "#/store/wallet/account/accountState";
import { type AccountStateByPortfolioSymbol } from "#/types/accounts";
import { PortfolioSymbol, type UnderlyingSymbol } from "#/types/markets";
import { formatCurrency } from "#/utils/valueFormatters";

import { DismissedAlertsDot } from "./components/DismissedAlertsDot";
import { PortfolioRiskDropdownItem } from "./components/PortfolioRiskDropdownItem/PortfolioRiskDropdownItem";

const createItems = (
  accountState: AccountStateByPortfolioSymbol,
): {
  id: string;
  icon?: KyanIcon;
  children: React.ReactNode;
  className?: string;
  imr?: number;
  mmr?: number;
  onClick?: () => void;
  disabled?: boolean;
  equity?: number;
}[] => [
  {
    id: PortfolioSymbol.BTC,
    icon: PortfolioSymbol.BTC.toLowerCase() as MarketIconSymbol,
    children: (
      <PortfolioRiskDropdownItem
        accountState={accountState.BTC}
        underlying={PortfolioSymbol.BTC}
      />
    ),
    imr: accountState.BTC.imr,
    mmr: accountState.BTC.mmr,
    equity: accountState.BTC.equity,
    onClick: () => {
      return null;
    },
  },
  {
    id: PortfolioSymbol.ETH,
    icon: PortfolioSymbol.ETH.toLowerCase() as MarketIconSymbol,
    imr: accountState.ETH.imr,
    mmr: accountState.ETH.mmr,
    children: (
      <PortfolioRiskDropdownItem
        accountState={accountState.ETH}
        underlying={PortfolioSymbol.ETH}
      />
    ),
    equity: accountState.ETH.equity,
  },
  {
    id: "unallocated",
    className: "bg-buttonHover h-9",
    disabled: true,
    children: (
      <Tooltip
        avoidCollisions
        side="top"
        align="center"
        content="Unallocated is not a selectable portfolio"
      >
        <PortfolioRiskDropdownItem
          accountState={accountState.USDC}
          underlying={PortfolioSymbol.USDC}
        />
      </Tooltip>
    ),
  },
];

export const PortfolioRiskDropdown = () => {
  const { isSm } = useMediaQuery();
  const { market, setMarketByUnderlying } = useMarket();
  const { accountState, portfolioState } = useAccountState();

  const items = createItems(accountState);

  const handleSelect = (value: UnderlyingSymbol) => {
    setMarketByUnderlying(value);
  };

  return (
    <Select
      variant="toolbar"
      value={market.underlying}
      triggerClassName="group w-57.5 xs:w-full sm:w-72.5 lg:border-r-0 h-full aria-expanded:bg-white dark:aria-expanded:bg-buttonHover md:max-2xl:w-62! xl:max-2xl:px-3"
      valueComponent={
        <div className="flex w-full flex-col">
          <div className="flex w-full items-center justify-between gap-1 pr-3">
            <div
              data-testid={`${market.underlying}-portfolio`}
              className="relative flex items-center gap-1.5 sm:gap-3"
            >
              <Icon
                name={market.underlying.toLowerCase() as MarketIconSymbol}
                className="text-textPrimary flex size-6 items-center justify-center transition-all"
              />
              <div className="grid place-items-start gap-1">
                <Typography
                  primary
                  size={isSm ? 14 : 12}
                  className="whitespace-nowrap"
                >
                  {market.underlying} Portfolio
                </Typography>
                <Typography size={isSm ? 14 : 12}>
                  {formatCurrency(portfolioState.equity)}
                </Typography>
              </div>

              <DismissedAlertsDot />
            </div>
            <div className="grid gap-1">
              <MarginRatio type="IMr" value={portfolioState.imr} />
              <MarginRatio value={portfolioState.mmr} />
            </div>
          </div>
        </div>
      }
      onValueChange={handleSelect}
    >
      {items.map((item) => (
        <SelectItem
          showCheck
          key={item.id}
          value={item.id}
          disabled={item.disabled}
          className={cn("h-auto p-4 text-xs [&>span]:w-full", item.className)}
        >
          <div
            className={cn("flex w-full items-center justify-between pr-6.5", {
              "cursor-default pr-0": item.id === "unallocated",
            })}
          >
            <span className="w-full text-xs">{item.children}</span>
            {item.imr !== undefined && item.mmr !== undefined && (
              <div className="grid gap-1">
                <MarginRatio type="IMr" value={item.imr} />
                <MarginRatio value={item.mmr} />
              </div>
            )}
          </div>
        </SelectItem>
      ))}
    </Select>
  );
};
